"use client";

import { useState } from "react";
import { useQueryAuthentications } from "@zapier/client";
import { ServiceIcon } from "@zapier/design-system";
import { useTranslations } from "@zapier/i18n";
import { getServiceIconUrl } from "@zapier/utils";
import {
  FilterMenuItem,
  FilterSelectMenu,
  MultiFilterOperator,
} from "@zapier/shared";

interface Props {
  selectedIds: string[];
  onToggle: (id: string, pendingOperator?: MultiFilterOperator) => void;
}

// Connection has only one operator (`is all of` → monolith `authentication_ids`,
// Postgres `__contains`). `FilterSelectMenu` hides its segmented control when
// `availableOperators.length < 2`.
const CONNECTION_OPERATORS: MultiFilterOperator[] = ["all"];

export function ConnectionFilterMenu({ selectedIds, onToggle }: Props) {
  const t = useTranslations("zap-filter-bar");
  const [search, setSearch] = useState("");
  const hasSearch = search.trim().length > 0;

  const { authentications, isLoading, isFetching } = useQueryAuthentications({
    search,
  });

  // Hydrate previously-selected connections so they render (and stay checked)
  // even when the current typeahead page doesn't surface them.
  // Zaps Connection filter stores authentication PKs as decimal strings.
  const selectedNumericIds = selectedIds.filter((id) => /^\d+$/.test(id));
  const { authentications: hydratedAuthentications } = useQueryAuthentications(
    {
      ids: selectedNumericIds,
      limit: Math.max(selectedNumericIds.length, 1),
    },
    { enabled: selectedNumericIds.length > 0 && !hasSearch },
  );

  const toMenuItem = (auth: {
    id?: number;
    title?: string | null;
    selectedApi?: string | null;
  }): FilterMenuItem | undefined => {
    if (auth.id === undefined) return undefined;
    const idAsString = String(auth.id);
    const displayName = auth.title || idAsString;
    const iconSlug = auth.selectedApi || "";
    return {
      id: idAsString,
      name: displayName,
      renderIcon: () => (
        <ServiceIcon
          serviceName={displayName}
          size="small"
          src={getServiceIconUrl(iconSlug)}
        />
      ),
    };
  };

  const byId = new Map<string, FilterMenuItem>();
  const ordered: string[] = [];
  const add = (auth: {
    id?: number;
    title?: string | null;
    selectedApi?: string | null;
  }) => {
    const item = toMenuItem(auth);
    if (!item || byId.has(item.id)) return;
    byId.set(item.id, item);
    ordered.push(item.id);
  };

  if (hasSearch) {
    // While searching show only matches — hydrated selections would muddle results.
    for (const auth of authentications) add(auth);
  } else {
    for (const auth of authentications) add(auth);
    for (const auth of hydratedAuthentications) add(auth);
  }

  const items = ordered.map((key) => byId.get(key)!);

  return (
    <FilterSelectMenu
      label={t("connection.label")}
      selectedIds={selectedIds}
      // Operator is fixed to `all`; the prop is required by the shared
      // component but never surfaces in the UI (single-operator menus hide
      // the segmented control).
      operator="all"
      availableOperators={CONNECTION_OPERATORS}
      items={items}
      search={search}
      isLoading={isLoading}
      isFetching={isFetching}
      searchPlaceholder={t("connection.search-placeholder")}
      noResultsText={
        hasSearch ? t("connection.no-results") : t("connection.search-prompt")
      }
      onToggle={onToggle}
      onSearchChange={setSearch}
    />
  );
}
